import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { EnterpriseContext } from "./EnterpriseContext";
import { SIDEBAR_WIDTH_CONSTRAINTS } from "@/components/SAMContainer/constants";

/**
 * Sidebar view types
 * - chat: Default view showing SAM chat interface
 * - map: Map drawer covering the chat area
 * - pinboard: Pinboard drawer covering the chat area
 */
export type SidebarView = "chat" | "map" | "pinboard";

// Re-export for backward compatibility
export { SIDEBAR_WIDTH_CONSTRAINTS };

const WIDTH_STORAGE_KEY = "sam-sidebar-width";

const getStoredWidth = (): number => {
  if (typeof window === "undefined") return SIDEBAR_WIDTH_CONSTRAINTS.DEFAULT;
  try {
    const stored = localStorage.getItem(WIDTH_STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!Number.isNaN(parsed)) {
        return Math.min(
          SIDEBAR_WIDTH_CONSTRAINTS.MAX,
          Math.max(SIDEBAR_WIDTH_CONSTRAINTS.MIN, parsed)
        );
      }
    }
  } catch {
    // Ignore localStorage errors
  }
  return SIDEBAR_WIDTH_CONSTRAINTS.DEFAULT;
};

interface SAMSidebarContextType {
  // Public API - used by pages/components
  openSidebar: (view?: SidebarView, message?: string) => void;
  closeSidebar: () => void;
  setSidebarView: (view: SidebarView) => void;
  injectMessage: (message: string) => void;
  openDrawerAndScrollTo: (itemId: string) => void;
  openPinboardWithAddModal: () => void;
  clearSelectedPinnedItem: () => void;
  isSidebarOpen: boolean;
  sidebarView: SidebarView;
  selectedPinnedItemId: string | null;
  shouldOpenAddModal: boolean;
  clearShouldOpenAddModal: () => void;
  // Target message for scrolling
  targetMessageId: string | null;
  setTargetMessageId: (messageId: string | null) => void;

  // Autofill status - shared between audit page and SAM container
  isAutofillActive: boolean;
  setIsAutofillActive: (active: boolean) => void;

  // Width resize API
  sidebarWidth: number;
  setSidebarWidth: (width: number) => void;
  resetSidebarWidth: () => void;
  isResizing: boolean;
  setIsResizing: (resizing: boolean) => void;

  // Input value persistence (survives sidebar close/open)
  inputValue: string;
  setInputValue: (value: string) => void;

  // Internal API - used by SAMSidebar to wire up implementations
  setSendMessageFn: (fn: (message: string) => Promise<void>) => void;
  setGetChatIdFn: (fn: () => string) => void;
  setInjectMessageFn: (fn: (message: string) => void) => void;
}

export const SAMSidebarContext =
  createContext<SAMSidebarContextType | undefined>(undefined);

export const useSAMSidebar = () => {
  const context = useContext(SAMSidebarContext);
  if (!context) {
    throw new Error("useSAMSidebar must be used within a SAMSidebarProvider");
  }
  return context;
};

/**
 * Optional version of useSAMSidebar that returns undefined
 * when used outside SAMSidebarProvider instead of throwing.
 * Use this for components that may be rendered outside the provider.
 */
export const useSAMSidebarOptional = () => {
  return useContext(SAMSidebarContext);
};

export type { SAMSidebarContextType };

interface SAMSidebarProviderProps {
  children: React.ReactNode;
}

const STORAGE_KEY = "sam-sidebar-state";

/**
 * Check if we have stored sidebar state in sessionStorage.
 * Used to determine if this is a first load (no stored state) or a return visit.
 */
const hasStoredState = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
};

/**
 * Get initial sidebar state from sessionStorage, or default values.
 * Default is closed (isOpen: false) - the provider will conditionally open
 * based on the newHomepage feature toggle for first-time visitors.
 */
const getInitialState = () => {
  if (typeof window === "undefined")
    return { isOpen: false, view: "chat" as SidebarView }; // SSR: default to closed
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        isOpen: parsed.isOpen ?? false,
        view: (parsed.view as SidebarView) ?? "chat",
      };
    }
  } catch {
    // Ignore parsing errors
  }
  return { isOpen: false, view: "chat" as SidebarView }; // First load: default to closed
};

export const SAMSidebarProvider: React.FC<SAMSidebarProviderProps> = ({
  children,
}) => {
  // Track if we had stored state (first load = no stored state)
  const hadStoredStateRef = useRef(hasStoredState());
  // Track if we've already initialized from feature toggle
  const hasInitializedFromToggleRef = useRef(false);

  // UI state - restored from sessionStorage
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    () => getInitialState().isOpen
  );
  const [sidebarView, setSidebarView] = useState<SidebarView>(
    () => getInitialState().view
  );
  const [selectedPinnedItemId, setSelectedPinnedItemId] =
    useState<string | null>(null);
  const [shouldOpenAddModal, setShouldOpenAddModal] = useState(false);
  const [targetMessageId, setTargetMessageId] = useState<string | null>(null);
  const [isAutofillActive, setIsAutofillActive] = useState(false);

  // Access EnterpriseContext to conditionally open sidebar based on feature toggle
  const enterpriseContext = useContext(EnterpriseContext);
  const isNewHomepageEnabled = enterpriseContext?.featureToggles?.newHomepage;

  /**
   * Initialize sidebar open state based on newHomepage feature toggle.
   * - If stored state exists: respect user's preference (don't change)
   * - If newHomepage=true and first load: open sidebar by default
   * - If newHomepage=false: keep closed (default)
   * This runs once when the feature toggle becomes available.
   */
  useEffect(() => {
    // Only run once
    if (hasInitializedFromToggleRef.current) return;

    // Wait for feature toggle to load (undefined means still loading)
    if (isNewHomepageEnabled === undefined) return;

    hasInitializedFromToggleRef.current = true;

    // If we had stored state, respect user preference (don't override)
    if (hadStoredStateRef.current) return;

    // First load with no stored state: set based on feature toggle
    if (isNewHomepageEnabled === true) {
      setIsSidebarOpen(true);
    }
    // If newHomepage is false, keep closed (already the default)
  }, [isNewHomepageEnabled]);

  // Width state - persisted to localStorage
  const [sidebarWidth, setSidebarWidthState] = useState(getStoredWidth);
  const [isResizing, setIsResizing] = useState(false);

  // Input value state - persisted across sidebar open/close
  const [inputValue, setInputValue] = useState("");

  // Persist width to localStorage
  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(WIDTH_STORAGE_KEY, String(sidebarWidth));
  }, [sidebarWidth]);

  const setSidebarWidth = useCallback((width: number) => {
    const clampedWidth = Math.min(
      SIDEBAR_WIDTH_CONSTRAINTS.MAX,
      Math.max(SIDEBAR_WIDTH_CONSTRAINTS.MIN, width)
    );
    setSidebarWidthState(clampedWidth);
  }, []);

  const resetSidebarWidth = useCallback(() => {
    setSidebarWidthState(SIDEBAR_WIDTH_CONSTRAINTS.DEFAULT);
  }, []);

  // Persist state to sessionStorage
  useEffect(() => {
    if (typeof window === "undefined") return;
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ isOpen: isSidebarOpen, view: sidebarView })
    );
  }, [isSidebarOpen, sidebarView]);

  // Function references from SAMChatContext (wired up by SAMSidebar)
  const [sendMessageFn, setSendMessageFn] =
    useState<((message: string) => Promise<void>) | null>(null);
  const [, /* getChatIdFn */ setGetChatIdFn] =
    useState<(() => string) | null>(null);
  const [injectMessageFn, setInjectMessageFn] =
    useState<((message: string) => void) | null>(null);

  // Pending message to send when sidebar opens
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  // Open sidebar and optionally set view and queue a message
  const openSidebar = useCallback((view?: SidebarView, message?: string) => {
    setIsSidebarOpen(true);

    if (view) {
      setSidebarView(view);
    } else {
      // Default to chat view if not specified
      setSidebarView("chat");
    }

    if (typeof message === "string" && message.trim()) {
      setPendingMessage(message);
    }
  }, []);

  // Close sidebar and reset view to chat
  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
    setSidebarView("chat");
  }, []);

  // Open pinboard drawer and scroll to specific item
  const openDrawerAndScrollTo = useCallback((itemId: string) => {
    setIsSidebarOpen(true);
    setSidebarView("pinboard");
    setSelectedPinnedItemId(itemId);
  }, []);

  // Open pinboard drawer with add widget modal open
  const openPinboardWithAddModal = useCallback(() => {
    setIsSidebarOpen(true);
    setSidebarView("pinboard");
    setShouldOpenAddModal(true);
  }, []);

  // Clear selected pinned item
  const clearSelectedPinnedItem = useCallback(() => {
    setSelectedPinnedItemId(null);
  }, []);

  // Clear should open add modal flag
  const clearShouldOpenAddModal = useCallback(() => {
    setShouldOpenAddModal(false);
  }, []);

  // Inject a message into chat (for autofill progress, etc.)
  const injectMessage = useCallback(
    (message: string) => {
      if (injectMessageFn) {
        injectMessageFn(message);
      } else {
        console.warn(
          "[SAMSidebarContext] injectMessage called before SAMSidebar initialized"
        );
      }
    },
    [injectMessageFn]
  );

  // Internal setters for SAMSidebar to wire up implementations
  const setSendMessage = useCallback(
    (fn: (message: string) => Promise<void>) => {
      setSendMessageFn(() => fn);
    },
    []
  );

  const setGetChatId = useCallback((fn: () => string) => {
    setGetChatIdFn(() => fn);
  }, []);

  const setInjectMessage = useCallback((fn: (message: string) => void) => {
    setInjectMessageFn(() => fn);
  }, []);

  // Send pending message when sendMessageFn becomes available
  useEffect(() => {
    if (pendingMessage && sendMessageFn) {
      sendMessageFn(pendingMessage);
      setPendingMessage(null);
    }
  }, [sendMessageFn, pendingMessage]);

  const value = useMemo(
    () => ({
      // Public API
      openSidebar,
      closeSidebar,
      setSidebarView,
      injectMessage,
      openDrawerAndScrollTo,
      openPinboardWithAddModal,
      clearSelectedPinnedItem,
      clearShouldOpenAddModal,
      isSidebarOpen,
      sidebarView,
      selectedPinnedItemId,
      shouldOpenAddModal,
      targetMessageId,
      setTargetMessageId,
      // Autofill status
      isAutofillActive,
      setIsAutofillActive,
      // Width resize API
      sidebarWidth,
      setSidebarWidth,
      resetSidebarWidth,
      isResizing,
      setIsResizing,
      // Input value persistence
      inputValue,
      setInputValue,
      // Internal API
      setSendMessageFn: setSendMessage,
      setGetChatIdFn: setGetChatId,
      setInjectMessageFn: setInjectMessage,
    }),
    [
      openSidebar,
      closeSidebar,
      setSidebarView,
      injectMessage,
      openDrawerAndScrollTo,
      openPinboardWithAddModal,
      clearSelectedPinnedItem,
      clearShouldOpenAddModal,
      isSidebarOpen,
      sidebarView,
      selectedPinnedItemId,
      shouldOpenAddModal,
      targetMessageId,
      isAutofillActive,
      sidebarWidth,
      setSidebarWidth,
      resetSidebarWidth,
      isResizing,
      inputValue,
      setSendMessage,
      setGetChatId,
      setInjectMessage,
    ]
  );

  return (
    <SAMSidebarContext.Provider value={value}>
      {children}
    </SAMSidebarContext.Provider>
  );
};

// Backward compatibility: export useSAM that maps to useSAMSidebar
// This allows existing code using useSAM() to continue working
export const useSAM = () => {
  const context = useSAMSidebar();
  return {
    openSAM: (message?: string) => context.openSidebar("chat", message),
    closeSAM: context.closeSidebar,
    injectMessage: context.injectMessage,
    isSAMOpen: context.isSidebarOpen,
    setSendMessageFn: context.setSendMessageFn,
    setGetChatIdFn: context.setGetChatIdFn,
    setInjectMessageFn: context.setInjectMessageFn,
  };
};
